import { environment } from "@/relay/environment";
import {
	createFileRoute,
	Outlet,
	useMatchRoute,
	useRouteContext,
} from "@tanstack/react-router";
import {
	fetchQuery,
	graphql,
	useLazyLoadQuery,
	useRefetchableFragment,
} from "react-relay";
import type { routeOnboardingClubQuery } from "./__generated__/routeOnboardingClubQuery.graphql";
import type { routeOnboardingQuery } from "./__generated__/routeOnboardingQuery.graphql";
import type { onboardingBatchJobsQuery } from "./__generated__/onboardingBatchJobsQuery.graphql";
import type { route_batchJobs$key } from "./__generated__/route_batchJobs.graphql";
import { useContext, useEffect, useState, useTransition } from "react";
import { Spinner } from "@/components/ui/spinner";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@/components/icon";
import {
	OnboardingThemeContext,
	OnboardingThemeProvider,
} from "@/utils/onboarding-theme";
import { ThemeLoader } from "@/utils/theme-loader";

export const Route = createFileRoute("/_logged/onboarding/$club")({
	component: RouteComponent,
	beforeLoad: async ({ params: { club: clubSlug } }) => {
		const data = await fetchQuery<routeOnboardingClubQuery>(
			environment,
			graphql`
        query routeOnboardingClubQuery($slug: String!) {
          club(slug: $slug) {
            id
            name
            slug
            logo {
              signedUrl
            }
            themes {
              originalTheme {
                name
              }
              styles
            }
          }
        }
      `,
			{ slug: clubSlug },
		).toPromise();

		return { club: data?.club };
	},
});

function OnboardingThemeContainer() {
	const { theme } = useContext(OnboardingThemeContext);

	const matchRoute = useMatchRoute();

	const renderDefaultTheme =
		matchRoute({ to: "/onboarding" }) ||
		matchRoute({ to: "/onboarding/$club/planos" }) ||
		matchRoute({ to: "/onboarding/$club/checkout" });

	if (renderDefaultTheme) {
		return null;
	}

	return <ThemeLoader colors={theme.styles} />;
}

function RouteComponent() {
	const { club } = useRouteContext({ from: Route.id });
	const [hasPendingJobs, setHasPendingJobs] = useState(false);
	const [showSuccessBanner, setShowSuccessBanner] = useState(false);
	const [isPending, startTransition] = useTransition();

	const clubData = useLazyLoadQuery<routeOnboardingQuery>(
		graphql`
    query routeOnboardingQuery($slug: String!) {
        club(slug: $slug) {
            ...route_batchJobs
        }
    }
  `,
		{ slug: club?.slug! },
	);

	const [data, refetch] = useRefetchableFragment<
		onboardingBatchJobsQuery,
		route_batchJobs$key
	>(
		graphql`
      fragment route_batchJobs on Club
				@refetchable(queryName: "onboardingBatchJobsQuery")
		  {
    		batchJobs(
      		status: PENDING
    		) {
          id
    		}
    	}
	`,
		clubData.club,
	);

	useEffect(() => {
		if (data.batchJobs.length > 0) {
			setHasPendingJobs(true);

			const timeout = setInterval(() => {
				startTransition(() => {
					refetch({}, { fetchPolicy: "network-only" });
				});
			}, 10000);

			return () => {
				clearInterval(timeout);
			};
		} else if (!data.batchJobs.length && hasPendingJobs) {
			setShowSuccessBanner(true);
		}
	}, [data.batchJobs, refetch, hasPendingJobs]);

	useEffect(() => {
		if (showSuccessBanner) {
			setTimeout(() => {
				setShowSuccessBanner(false);
			}, 30000);
		}
	}, [showSuccessBanner]);

	return (
		<OnboardingThemeProvider
			initialState={
				club?.themes?.length
					? {
							name: club?.themes[0]?.originalTheme.name,
							styles: club?.themes[0]?.styles,
						}
					: undefined
			}
		>
			<OnboardingThemeContainer />

			<Outlet />

			<AnimatePresence>
				{data.batchJobs.length > 0 || isPending || showSuccessBanner ? (
					<motion.div
						className="bg-neutral-950 items-center justify-center rounded-full flex flex-row gap-3 px-6 py-1 absolute bottom-4 left-1/2 -translate-x-1/2"
						initial={{ y: 25, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						exit={{ y: 25, opacity: 0 }}
					>
						{data.batchJobs.length > 0 || isPending ? (
							<Spinner className="text-primary size-6" />
						) : (
							<div className="size-6 bg-green-200 rounded-full flex items-center justify-center">
								<Icon name="Check" className="size-4 text-green-600" />
							</div>
						)}

						<div className="flex flex-col">
							{data.batchJobs.length > 0 || isPending ? (
								<>
									<span className="text-sm/5 text-white">Importando Curso</span>
									<span className="text-white/60 text-xs/5">
										Você pode continuar navegando
									</span>
								</>
							) : (
								<>
									<span className="text-sm/5 text-white">
										Migração Concluída com sucesso!
									</span>
									<span className="text-white/60 text-xs/5">
										Seu curso foi importado e está pronto para uso.
									</span>
								</>
							)}
						</div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</OnboardingThemeProvider>
	);
}
